(function () {
  const INSIGHTS_ENDPOINT = 'https://script.google.com/macros/s/AKfycbx90jmuEbs4BgqteWutX_ZzokpyaaNdpN2o5M6_jPMtk0V-5s5LIzQJeKSUl7j4FBPm/exec';
  const PREDICTIVE_LIMIT = 4;
  const SEARCH_PAGE_LIMIT = 12;
  const PREDICTIVE_GROUP_SELECTOR = '[data-insights-predictive-group]';

  let longevityBlogPromise = null;

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character];
    });
  }

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function isValidImageUrl(value) {
    return /^https?:\/\//i.test(String(value || ''));
  }

  function getCurrentLanguageCode() {
    const htmlLang = normalize(document.documentElement.getAttribute('lang')).split('-')[0];
    if (htmlLang) return htmlLang;

    const pathLanguage = normalize(window.location.pathname.split('/').filter(Boolean)[0]);
    return ['en', 'de', 'fr', 'it', 'es'].includes(pathLanguage) ? pathLanguage : 'en';
  }

  function getCurrentLanguageMatches() {
    const languageMap = {
      en: ['en', 'english'],
      de: ['de', 'deutsch', 'german'],
      fr: ['fr', 'francais', 'french'],
      it: ['it', 'italian', 'italiano'],
      es: ['es', 'spanish', 'espanol'],
    };

    const currentLanguage = getCurrentLanguageCode();
    return languageMap[currentLanguage] || [currentLanguage];
  }

  function getCurrentMarketMatches() {
    const html = document.documentElement;
    const country = normalize(html.getAttribute('country'));
    const market = normalize(html.getAttribute('market'));
    const pathPrefix = normalize(window.location.pathname.split('/').filter(Boolean)[0]);

    const marketAliases = {
      gb: ['gb', 'uk', 'united-kingdom', 'united kingdom', 'great-britain', 'great britain'],
      ch: ['ch', 'switzerland', 'switzerland-chf', 'swiss'],
      it: ['it', 'italy', 'italia', 'italy-eur'],
    };

    const matches = new Set([country, market, pathPrefix].filter(Boolean));
    Object.entries(marketAliases).forEach(([code, aliases]) => {
      if (matches.has(code) || aliases.some((alias) => matches.has(alias))) {
        aliases.forEach((alias) => matches.add(alias));
        matches.add(code);
      }
    });

    return matches;
  }

  function isCurrentLanguageItem(item) {
    const language = normalize(item.language);
    if (!language) return false;
    if (['all', '*'].includes(language)) return true;

    const itemLanguages = language.split(/[,|;/]+/).map((value) => value.trim()).filter(Boolean);
    const currentLanguageMatches = getCurrentLanguageMatches();

    return itemLanguages.some((itemLanguage) => currentLanguageMatches.includes(itemLanguage));
  }

  function parseList(value) {
    return normalize(value)
      .split(/[,|;/]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  function getExcludedMarkets(item) {
    return parseList(
      item.excluded_markets ||
        item.excluded_market ||
        item.exclude_markets ||
        item.exclude_market ||
        item.excluded_countries ||
        item.excluded_country ||
        item.exclude_countries ||
        item.exclude_country ||
        item.market_exclusions ||
        item.country_exclusions
    );
  }

  function isCurrentMarketAllowed(item) {
    const excludedMarkets = getExcludedMarkets(item);
    if (!excludedMarkets.length) return true;

    const currentMarketMatches = getCurrentMarketMatches();
    return !excludedMarkets.some((market) => currentMarketMatches.has(market));
  }

  function getSearchTokens(query) {
    return normalize(query)
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length > 1);
  }

  function fetchLongevityBlogItems() {
    if (!longevityBlogPromise) {
      longevityBlogPromise = fetch(INSIGHTS_ENDPOINT, { cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error(`Longevity Blog search failed: ${response.status}`);
          return response.json();
        })
        .then((items) => {
          if (!Array.isArray(items)) return [];

          return items
            .filter((item) => item && item.url && item.title && item.active !== false)
            .map((item) => ({
              url: String(item.url || '').trim(),
              title: String(item.title || '').trim(),
              meta_description: String(item.meta_description || '').trim(),
              main_image_url: String(item.main_image_url || '').trim(),
              language: String(item.language || '').trim(),
              keywords: String(item.keywords || '').trim(),
              excluded_markets: String(
                item.excluded_markets ||
                  item.excluded_market ||
                  item.exclude_markets ||
                  item.exclude_market ||
                  item.excluded_countries ||
                  item.excluded_country ||
                  item.exclude_countries ||
                  item.exclude_country ||
                  item.market_exclusions ||
                  item.country_exclusions ||
                  ''
              ).trim(),
              priority: Number(item.priority || 0),
            }))
            .filter(isCurrentLanguageItem)
            .filter(isCurrentMarketAllowed);
        })
        .catch((error) => {
          console.warn(error);
          return [];
        });
    }

    return longevityBlogPromise;
  }

  function scoreInsight(item, query, tokens) {
    const normalizedQuery = normalize(query);
    const fields = {
      title: normalize(item.title),
      description: normalize(item.meta_description),
      url: normalize(item.url),
      keywords: normalize(item.keywords),
    };
    const haystack = Object.values(fields).join(' ');
    let score = Number(item.priority || 0);

    if (!normalizedQuery || !tokens.length) return 0;
    if (!tokens.some((token) => haystack.includes(token)) && !haystack.includes(normalizedQuery)) return 0;

    if (fields.title.includes(normalizedQuery)) score += 80;
    if (fields.keywords.includes(normalizedQuery)) score += 60;
    if (fields.description.includes(normalizedQuery)) score += 40;
    if (fields.url.includes(normalizedQuery)) score += 20;

    tokens.forEach((token) => {
      if (fields.title.includes(token)) score += 12;
      if (fields.keywords.includes(token)) score += 10;
      if (fields.description.includes(token)) score += 6;
      if (fields.url.includes(token)) score += 3;
    });

    return score;
  }

  function findLongevityBlogItems(query, limit) {
    const tokens = getSearchTokens(query);

    return fetchLongevityBlogItems().then((items) => {
      return items
        .map((item) => ({ item, score: scoreInsight(item, query, tokens) }))
        .filter((result) => result.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((result) => result.item);
    });
  }

  function renderPredictiveItem(item, index) {
    const imageMarkup = isValidImageUrl(item.main_image_url)
      ? `<div class="predictive-search__product-image insights-predictive-search__image"><img src="${escapeHtml(item.main_image_url)}" alt="" loading="lazy"></div>`
      : '<div class="predictive-search__product-image insights-predictive-search__image insights-predictive-search__image--empty"></div>';

    return `
      <div class="predictive-search__item" id="predictiveSearchOptionLongevityBlog-${index + 1}" role="option" aria-selected="false">
        <a href="${escapeHtml(item.url)}" class="predictive-search__product insights-predictive-search__link" tabindex="-1">
          ${imageMarkup}
          <p class="predictive-search__product-title">${escapeHtml(item.title)}</p>
        </a>
      </div>
    `;
  }

  function renderPredictiveGroup(items) {
    if (!items.length) return '';

    return `
      <div class="predictive-search__group insights-predictive-search" role="group" aria-labelledby="predictiveSearchLongevityBlog" data-insights-predictive-group>
        <p id="predictiveSearchLongevityBlog" class="predictive-search__heading label-typography">
          <span>Longevity Blog</span>
        </p>
        <div class="predictive-search__list" role="listbox">
          ${items.map(renderPredictiveItem).join('')}
        </div>
      </div>
    `;
  }

  function updatePredictiveSearch(predictiveSearch) {
    const input = predictiveSearch.querySelector('input[type="search"]');
    const resultsRoot = predictiveSearch.querySelector('[data-predictive-search-results]');
    const resultsWrapper = predictiveSearch.querySelector('[data-search-results-wrapper]') || resultsRoot;
    const query = input ? input.value : '';

    if (!resultsRoot || !resultsWrapper || !query.trim()) return;

    const currentGroup = resultsRoot.querySelector(PREDICTIVE_GROUP_SELECTOR);
    if (currentGroup && currentGroup.dataset.insightsQuery === query) return;
    if (resultsRoot.dataset.insightsSearchPending === query) return;

    resultsRoot.dataset.insightsSearchPending = query;
    resultsRoot.querySelectorAll(PREDICTIVE_GROUP_SELECTOR).forEach((group) => group.remove());

    findLongevityBlogItems(query, PREDICTIVE_LIMIT).then((items) => {
      delete resultsRoot.dataset.insightsSearchPending;
      if ((input ? input.value : '') !== query) return;

      resultsRoot.querySelectorAll(PREDICTIVE_GROUP_SELECTOR).forEach((group) => group.remove());

      const markup = renderPredictiveGroup(items);
      if (!markup) return;

      const actions = resultsWrapper.querySelector('.predictive-search__actions');
      if (actions) {
        actions.insertAdjacentHTML('beforebegin', markup);
      } else {
        resultsWrapper.insertAdjacentHTML('beforeend', markup);
      }

      const insertedGroup = resultsRoot.querySelector(PREDICTIVE_GROUP_SELECTOR);
      if (insertedGroup) insertedGroup.dataset.insightsQuery = query;
    });
  }

  function initPredictiveSearch() {
    document.querySelectorAll('predictive-search').forEach((predictiveSearch) => {
      const resultsRoot = predictiveSearch.querySelector('[data-predictive-search-results]');
      if (!resultsRoot || predictiveSearch.dataset.insightsSearchBound === 'true') return;

      predictiveSearch.dataset.insightsSearchBound = 'true';

      const observer = new MutationObserver(() => {
        window.requestAnimationFrame(() => updatePredictiveSearch(predictiveSearch));
      });

      observer.observe(resultsRoot, { childList: true, subtree: true });
      predictiveSearch.addEventListener('input', () => {
        window.requestAnimationFrame(() => updatePredictiveSearch(predictiveSearch));
      });
    });
  }

  function renderSearchPageCard(item) {
    const imageMarkup = isValidImageUrl(item.main_image_url)
      ? `<img src="${escapeHtml(item.main_image_url)}" alt="" loading="lazy">`
      : '';

    return `
      <div class="product-grid-item grid__item one-third mobile--one-whole insights-search-card" data-insights-search-page-results data-product-block>
        <div class="product-grid-item__image insights-search-card__media" data-product-media-container>
          <a class="product__media__holder insights-search-card__image${imageMarkup ? '' : ' insights-search-card__image--empty'}" href="${escapeHtml(item.url)}" aria-label="${escapeHtml(item.title)}">
            ${imageMarkup}
          </a>
          <span class="insights-search-card__badge">Longevity Blog</span>
        </div>
        <div class="product-grid-item__info product-grid-item__info--left insights-search-card__content">
          <a class="product-grid-item__title insights-search-card__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          ${item.meta_description ? `<a class="product-cutline insights-search-card__description" href="${escapeHtml(item.url)}">${escapeHtml(item.meta_description)}</a>` : ''}
        </div>
      </div>
    `;
  }

  function getSearchPageGridTargets(searchPage) {
    return Array.from(
      searchPage.querySelectorAll('[data-all-types-container] .collection__products, [data-tab="resultsProducts"] .collection__products')
    );
  }

  function ensureSearchPageGrid(container) {
    const existingGrid = container.querySelector('.grid');
    if (existingGrid) return existingGrid;

    const emptyState = container.querySelector('.collection--no-results, .search-page__note');
    if (emptyState) emptyState.hidden = true;

    const grid = document.createElement('div');
    grid.className = 'grid';
    grid.setAttribute('data-insights-created-grid', '');
    container.insertBefore(grid, container.firstChild);

    return grid;
  }

  function insertSearchPageCards(grid, markup) {
    grid.querySelectorAll('[data-insights-search-page-results]').forEach((result) => result.remove());

    const lastProduct = Array.from(grid.querySelectorAll('.product-grid-item:not(.insights-search-card)')).pop();
    if (lastProduct) {
      lastProduct.insertAdjacentHTML('afterend', markup);
      return;
    }

    grid.insertAdjacentHTML('beforeend', markup);
  }

  function initSearchPage() {
    const searchPage = document.getElementById('SearchPage');
    if (!searchPage || searchPage.dataset.insightsSearchBound === 'true') return;

    const query = new URLSearchParams(window.location.search).get('q') || '';
    if (!query.trim()) return;

    searchPage.dataset.insightsSearchBound = 'true';

    findLongevityBlogItems(query, SEARCH_PAGE_LIMIT).then((items) => {
      searchPage.querySelectorAll('[data-insights-search-page-results]').forEach((result) => result.remove());
      if (!items.length) return;

      const markup = items.map(renderSearchPageCard).join('');
      getSearchPageGridTargets(searchPage).forEach((container) => {
        insertSearchPageCards(ensureSearchPageGrid(container), markup);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initPredictiveSearch();
    initSearchPage();
  });

  document.addEventListener('shopify:section:load', () => {
    initPredictiveSearch();
    initSearchPage();
  });
})();
